import { Icon, IconSize } from "../../Atoms/Icons/Icon/Icon";

import styles from "./IconBadge.module.scss";

import ArrowsClockWise from "../../Atoms/Icons/Types/ArrowsClockwise/arrows-clockwise-bold.svg";
import Check from "../../Atoms/Icons/Types/Check/check-bold.svg";
import CloudCheck from "../../Atoms/Icons/Types/CloudCheck/cloud-check-fill.svg";
import Info from "../../Atoms/Icons/Types/Info/info-bold.svg";
import MedfarWarningSolo from "../../Atoms/Icons/Types/MedfarWarningSolo/medfar-warning-solo.svg";
import QuestionMark from "../../Atoms/Icons/Types/QuestionMark/question-mark-regular.svg";
import WarningFill from "../../Atoms/Icons/Types/Warning/warning-fill.svg";
import X from "../../Atoms/Icons/Types/X/x-bold.svg";

type IconBadgeVariant = "error" | "warning" | "success" | "help" | "information" | "inProgress" | "uploadSuccess" | "alarming";
type IconBadgeSize = "lg" | "md" | "sm" | "xs";

interface IIconBadgeProps {
    /**
     * The optional Badge ID.
     */
    id?: string;

    /**
     * The Badge varient.
     */
    variant: IconBadgeVariant;

    /**
     * The size of the icon and badge.
     */
    size: IconBadgeSize;

    /**
     * An attribute to be use for the testing purpose.
     */
    dataCy?: string;
}

const variantMapper: Record<IconBadgeVariant, string> = {
    "alarming": `${styles.iconBadge} ${styles.alarming}`,
    "error": `${styles.iconBadge} ${styles.error}`,
    "help": `${styles.iconBadge} ${styles.help}`,
    "inProgress": `${styles.iconBadge} ${styles.inProgress}`,
    "information": `${styles.iconBadge} ${styles.information}`,
    "success": `${styles.iconBadge} ${styles.success}`,
    "uploadSuccess": `${styles.iconBadge} ${styles.uploadSuccess}`,
    "warning": `${styles.iconBadge} ${styles.warning}`
} as const;

const IconMapper: Record<IconBadgeVariant, JSX.Element> = {
    "alarming": <WarningFill />,
    "error": <X />,
    "help": <QuestionMark />,
    "inProgress": <ArrowsClockWise />,
    "information": <Info />,
    "success": <Check />,
    "uploadSuccess": <CloudCheck />,
    "warning": <MedfarWarningSolo />
} as const;

const badgeSizeMapper: Record<IconBadgeSize, Exclude<IconSize, "lg">> = {
    "lg": "md",
    "md": "sm",
    "sm": "xs",
    "xs": "xxs"
} as const;

const keepIconSize: IconBadgeVariant[] = ["uploadSuccess", "alarming"];

/**
 * The icon badge.
 * @param {IIconBadgeProps} param The props used to render the icon badge.
 * @returns The rendered component.
 */
function IconBadge({
    id,
    variant = "information",
    size = "md",
    dataCy
}: Readonly<IIconBadgeProps>): JSX.Element {
    return (
        <span
            id={id}
            className={`${variantMapper[variant]} ${styles[size]}`}
            data-cy={dataCy}
        >
            <Icon size={keepIconSize.includes(variant) ? size : badgeSizeMapper[size]}>
                {IconMapper[variant]}
            </Icon>
        </span>
    );
}

export { IconBadge, IIconBadgeProps };
